import { todayISO } from "@/lib/utils";

/**
 * Which occurrence of a recurring checklist a completion belongs to.
 *
 * HQ-CHECKLIST-001. Completions were read with `completed_on = today` whatever
 * the checklist's frequency said, so a weekly list forgot every tick made
 * earlier in the same week: two items done on Monday and Wednesday showed as
 * 50% on Wednesday with Monday's item unchecked. `once` had the same problem in
 * a worse form — a one-time task reappeared every day forever.
 *
 * A completion now records the occurrence it satisfies, not merely the day it
 * happened, and the database makes that occurrence unique per person per item.
 */
export type Frequency = "once" | "daily" | "weekly" | "shift_open" | "shift_close";

/** Sunday, matching the Sunday-to-Saturday workweek the handbook defines for
 *  overtime — so "this week" means the same thing in both places. */
export function startOfBusinessWeek(d: Date = new Date()): Date {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  start.setDate(start.getDate() - start.getDay());
  return start;
}

/**
 * The occurrence key for a frequency, as of `now` in the viewer's local time.
 *
 * Local, deliberately: a checklist belongs to a working day, and a UTC day
 * boundary would roll the evening shift's list over to tomorrow (the same fault
 * as DATE-001).
 */
export function periodKey(frequency: string, now: Date = new Date()): string {
  switch (frequency) {
    // Done is done. One key forever, so it never comes back.
    case "once":
      return "once";
    case "weekly":
      return `W:${todayISO(startOfBusinessWeek(now))}`;
    // Two separate occurrences within one day. This is a stand-in: HQ has no
    // shift model, so opening and closing are distinguished by name rather than
    // by which shift they belong to. A real shift instance should replace the
    // date half of this key when one exists.
    case "shift_open":
      return `${todayISO(now)}:open`;
    case "shift_close":
      return `${todayISO(now)}:close`;
    case "daily":
    default:
      return todayISO(now);
  }
}

/** What the current occurrence should be called on screen. */
export function periodLabel(frequency: string, now: Date = new Date()): string {
  switch (frequency) {
    case "once": return "One time";
    case "weekly": {
      const start = startOfBusinessWeek(now);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `Week of ${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
    }
    case "shift_open": return "Shift open";
    case "shift_close": return "Shift close";
    default: return "Today";
  }
}

/**
 * The earliest completion date worth fetching to decide every listed
 * frequency's current occurrence. `once` is unbounded and handled separately.
 */
export function earliestRelevantDate(frequencies: string[], now: Date = new Date()): string {
  return frequencies.includes("weekly")
    ? todayISO(startOfBusinessWeek(now))
    : todayISO(now);
}
